
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { useToast } from '@/hooks/use-toast';
import { SubscriptionDetails, SubscriptionTier } from '@/types/subscription';

interface Subscription {
  tier_id: string;
  tier_name: string;
  max_audio_video_duration: number;
  is_active: boolean;
  billing_cycle: string;
  features: any;
  subscription_end: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
}

interface SubscriptionContextType {
  subscription: Subscription | null;
  isLoading: boolean;
  error: any;
  refreshSubscription: () => Promise<void>;
  availableTiers: SubscriptionTier[];
  switchTier: (tierId: string, billingCycle: 'monthly' | 'yearly') => Promise<void>;
  isSwitchingTier: boolean;
  shouldContactSales: (tierName: string) => boolean;
}

const SubscriptionContext = createContext<SubscriptionContextType>({
  subscription: null,
  isLoading: false,
  error: null,
  refreshSubscription: async () => {},
  availableTiers: [],
  switchTier: async () => {},
  isSwitchingTier: false,
  shouldContactSales: () => false,
});

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [availableTiers, setAvailableTiers] = useState<SubscriptionTier[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSwitchingTier, setIsSwitchingTier] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchSubscription = async (userId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase.rpc('get_user_subscription', {
        user_uuid: userId,
      });

      if (fetchError) {
        console.error('Error fetching subscription:', fetchError);
        setError(fetchError);
        return null;
      }

      console.log('Subscription data:', data);
      return data;
    } catch (err) {
      console.error('Unexpected error in fetchSubscription:', err);
      setError(err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableTiers = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_tiers')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error fetching tiers:', error);
        return [];
      }

      return data as SubscriptionTier[];
    } catch (err) {
      console.error('Error in fetchAvailableTiers:', err);
      return [];
    }
  };

  const switchTier = async (tierId: string, billingCycle: 'monthly' | 'yearly') => {
    if (!user?.id) return;
    
    try {
      setIsSwitchingTier(true);
      
      // For now, this is a placeholder for the actual tier switching logic
      toast({
        title: "Subscription change requested",
        description: "Your subscription change request is being processed.",
      });
      
      // Here you would implement the actual tier switching logic
      // For example, calling a Supabase function or updating the database
      
      // Refresh the subscription data after switching
      await refreshSubscription();
      
      toast({
        title: "Subscription updated",
        description: "Your subscription has been successfully updated.",
      });
    } catch (err) {
      console.error('Error switching tier:', err);
      toast({
        title: "Error updating subscription",
        description: "There was a problem updating your subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSwitchingTier(false);
    }
  };

  const shouldContactSales = (tierName: string) => {
    return tierName === "Enterprise";
  };

  const refreshSubscription = async () => {
    if (!user?.id) return;
    
    try {
      const subscriptionData = await fetchSubscription(user.id);
      if (subscriptionData) {
        // Fix: Set subscription to the first item in the array or the item itself
        setSubscription(Array.isArray(subscriptionData) ? subscriptionData[0] : subscriptionData);
      }
      
      const tiers = await fetchAvailableTiers();
      setAvailableTiers(tiers);
    } catch (err) {
      console.error('Error refreshing subscription:', err);
      toast({
        title: 'Error refreshing subscription',
        description: 'Please try again later',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    const loadSubscription = async () => {
      if (!user?.id) {
        setSubscription(null);
        setIsLoading(false);
        return;
      }

      try {
        const subscriptionData = await fetchSubscription(user.id);
        if (subscriptionData) {
          // Fix: Set subscription to the first item in the array or the item itself
          setSubscription(Array.isArray(subscriptionData) ? subscriptionData[0] : subscriptionData);
        }
        
        const tiers = await fetchAvailableTiers();
        setAvailableTiers(tiers);
      } catch (err) {
        console.error('Error loading subscription:', err);
      }
    };

    loadSubscription();
  }, [user]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        isLoading,
        error,
        refreshSubscription,
        availableTiers,
        switchTier,
        isSwitchingTier,
        shouldContactSales,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

